import {FieldType, useAllFieldsQuery} from '@transactions/graphql';
import Fuse from 'fuse.js';
import * as React from 'react';
import {FieldPill} from 'src/inputs/FieldInput/FieldPill';
import {TextFieldPill} from 'src/inputs/FieldInput/TextFieldPill';
import {TextInput} from 'src/inputs/TextInput';
import {Popover} from 'src/Popover/NewPopover';
import {PopoverContents, PopoverItem} from 'src/Popover/views';
import type {FocusControls} from 'src/FocusTrap';

type FieldSearchResult = {
  fieldId: string;
  fieldName: string;
  value: string;
  fieldType: FieldType;
};

type Props = {
  fieldValues: Record<string, string>;
  onChange: (fieldValues: Record<string, string>) => void;
  focusControlsRef?: React.MutableRefObject<FocusControls | null>;
};

export const FieldInput = (props: Props) => {
  const {fieldValues, onChange, focusControlsRef} = props;
  const {data} = useAllFieldsQuery();

  const [query, setQuery] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const activeItemRef = React.useRef<HTMLDivElement | null>(null);

  const fields = data?.allFields || [];

  // Create Fuse instance for searching field names
  const fieldFuse = React.useMemo(
    () => new Fuse(fields, {keys: ['name'], threshold: 0.4}),
    [fields],
  );

  // Separate enum and text fields
  const enumFields = React.useMemo(
    () => fields.filter((f) => f.type === FieldType.Enum),
    [fields],
  );
  const textFields = React.useMemo(
    () => fields.filter((f) => f.type === FieldType.Text),
    [fields],
  );

  // Fuse instance for text field name searching
  const textFieldFuse = React.useMemo(
    () => new Fuse(textFields, {keys: ['name'], threshold: 0.4}),
    [textFields],
  );

  // Flat list of all field:value pairs for value searching (enum fields only)
  const allValueItems = React.useMemo(
    () =>
      enumFields.flatMap((dim) =>
        dim.values.map((val) => ({
          fieldId: dim._id,
          fieldName: dim.name,
          value: val.value,
          fieldType: FieldType.Enum,
        })),
      ),
    [enumFields],
  );

  // Single Fuse instance for searching across all values (no-colon path)
  const valueFuse = React.useMemo(
    () => new Fuse(allValueItems, {keys: ['value'], threshold: 0.4}),
    [allValueItems],
  );

  // Per-field Fuse instances for value searching (colon path)
  const perFieldValueFuse = React.useMemo(
    () =>
      Object.fromEntries(
        fields.map((dim) => [
          dim._id,
          new Fuse(dim.values, {keys: ['value'], threshold: 0.4}),
        ]),
      ),
    [fields],
  );

  const searchResults = React.useMemo((): FieldSearchResult[] => {
    if (!query.trim()) {
      return [];
    }

    const colonIndex = query.indexOf(':');

    if (colonIndex === -1) {
      // No colon - search field names AND values
      const keyQuery = query.trim();
      const seen = new Set<string>();
      const results: FieldSearchResult[] = [];

      const addResult = (result: FieldSearchResult) => {
        const key = `${result.fieldId}:${result.value}`;
        if (seen.has(key)) return;
        seen.add(key);
        results.push(result);
      };

      // Match by enum field name - show all values from matching fields
      const nameMatches = keyQuery
        ? fieldFuse.search(keyQuery).map((r) => r.item)
        : fields;
      for (const dim of nameMatches) {
        if (dim._id in fieldValues) continue;
        if (dim.type === FieldType.Text) continue; // Text fields handled separately
        for (const val of dim.values) {
          addResult({
            fieldId: dim._id,
            fieldName: dim.name,
            value: val.value,
            fieldType: FieldType.Enum,
          });
        }
      }

      // Match by field value - search values across all enum fields
      if (keyQuery) {
        for (const match of valueFuse.search(keyQuery)) {
          if (match.item.fieldId in fieldValues) continue;
          addResult(match.item);
        }
      }

      // Match text fields by name
      const textMatches = keyQuery
        ? textFieldFuse.search(keyQuery).map((r) => r.item)
        : textFields;
      for (const tf of textMatches) {
        if (tf._id in fieldValues) continue;
        addResult({
          fieldId: tf._id,
          fieldName: tf.name,
          value: '',
          fieldType: FieldType.Text,
        });
      }

      return results.slice(0, 15);
    } else {
      // Has colon - search both parts
      const keyQuery = query.slice(0, colonIndex).trim();
      const valueQuery = query.slice(colonIndex + 1).trim();

      // First filter by field name (enum fields only for colon search)
      const matchedFields = keyQuery
        ? fieldFuse
            .search(keyQuery)
            .map((r) => r.item)
            .filter((f) => f.type === FieldType.Enum)
        : enumFields;

      const results: FieldSearchResult[] = [];
      for (const dim of matchedFields) {
        if (dim._id in fieldValues) continue;

        // Then filter values if valueQuery exists
        let matchedValues = dim.values;
        if (valueQuery) {
          const fuse = perFieldValueFuse[dim._id];
          matchedValues = fuse
            ? fuse.search(valueQuery).map((r) => r.item)
            : [];
        }

        for (const val of matchedValues) {
          results.push({
            fieldId: dim._id,
            fieldName: dim.name,
            value: val.value,
            fieldType: FieldType.Enum,
          });
        }
      }
      return results.slice(0, 15);
    }
  }, [
    query,
    fields,
    enumFields,
    textFields,
    fieldFuse,
    textFieldFuse,
    valueFuse,
    perFieldValueFuse,
    fieldValues,
  ]);

  // Reset selection when results change
  React.useEffect(() => {
    setSelectedIndex(null);
  }, [searchResults.length]);

  // Scroll active item into view
  React.useLayoutEffect(() => {
    activeItemRef.current?.scrollIntoView({block: 'nearest'});
  }, [selectedIndex]);

  // Set popover width
  React.useLayoutEffect(() => {
    if (isOpen && inputRef.current && popoverRef.current) {
      popoverRef.current.style.width = `${Math.max(inputRef.current.offsetWidth, 250)}px`;
    }
  }, [isOpen]);

  // Track which text field is currently being edited inline
  const [editingTextFieldId, setEditingTextFieldId] = React.useState<
    string | null
  >(null);

  const totalSelectableItems = searchResults.length;

  const handleRemove = (fieldId: string) => {
    const newValues = {...fieldValues};
    delete newValues[fieldId];
    onChange(newValues);
    if (editingTextFieldId === fieldId) {
      setEditingTextFieldId(null);
    }
  };

  const handleSelect = (result: FieldSearchResult) => {
    if (result.fieldType === FieldType.Text) {
      // For text fields, add with empty string and start editing
      onChange({
        ...fieldValues,
        [result.fieldId]: '',
      });
      setEditingTextFieldId(result.fieldId);
    } else {
      onChange({
        ...fieldValues,
        [result.fieldId]: result.value,
      });
    }
    setQuery('');
    setIsOpen(false);
    setSelectedIndex(null);
    // Keep focus for adding more (for enum fields)
    if (result.fieldType !== FieldType.Text) {
      inputRef.current?.focus();
    }
  };

  const handleInputChange = (value: string) => {
    setQuery(value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    if (query) {
      setIsOpen(true);
    }
  };

  const handleSelectOrCreate = (index: number | null) => {
    const resultIndex = index ?? 0;
    if (searchResults[resultIndex]) {
      handleSelect(searchResults[resultIndex]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen && query) {
        setIsOpen(true);
      } else if (totalSelectableItems > 0) {
        setSelectedIndex((prev) =>
          prev === null ? 0 : Math.min(prev + 1, totalSelectableItems - 1),
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (totalSelectableItems > 0 && selectedIndex !== null) {
        setSelectedIndex((prev) => (prev === 0 ? null : (prev ?? 0) - 1));
      }
    } else if (e.key === 'Enter') {
      if (isOpen && totalSelectableItems > 0) {
        e.preventDefault();
        handleSelectOrCreate(selectedIndex);
      }
      // When popover is closed, allow Enter to propagate for form submission
    } else if (e.key === 'Tab') {
      if (!query) {
        // Empty input - let Tab pass through
        setIsOpen(false);
        return;
      }
      // Has query - select and move on
      e.preventDefault();
      if (selectedIndex !== null) {
        handleSelectOrCreate(selectedIndex);
      } else if (searchResults.length > 0) {
        handleSelect(searchResults[0]);
      }
      if (e.shiftKey) {
        focusControlsRef?.current?.focusBackward();
      } else {
        focusControlsRef?.current?.focusForward();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setQuery('');
      setIsOpen(false);
      setSelectedIndex(null);
    }
  };

  const closePopover = () => {
    setIsOpen(false);
  };

  // Get field entries for pills
  const fieldEntries = Object.entries(fieldValues).map(
    ([fieldId, value]) => {
      const field = fields.find((d) => d._id === fieldId);
      return {
        fieldId,
        fieldName: field?.name || fieldId,
        fieldType: field?.type ?? FieldType.Enum,
        value,
      };
    },
  );

  const handleTextFieldCommit = (fieldId: string, value: string) => {
    if (value.trim()) {
      onChange({
        ...fieldValues,
        [fieldId]: value.trim(),
      });
    } else {
      // Remove if empty
      const newValues = {...fieldValues};
      delete newValues[fieldId];
      onChange(newValues);
    }
    setEditingTextFieldId(null);
    inputRef.current?.focus();
  };

  const showPopover = isOpen && searchResults.length > 0;

  return (
    <div className="flex flex-col gap-2">
      {/* Pills for existing fields */}
      {fieldEntries.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {fieldEntries.map((entry) =>
            entry.fieldType === FieldType.Text ? (
              <TextFieldPill
                key={entry.fieldId}
                fieldName={entry.fieldName}
                value={entry.value}
                isEditing={editingTextFieldId === entry.fieldId}
                onCommit={(value) =>
                  handleTextFieldCommit(entry.fieldId, value)
                }
                onStartEdit={() => setEditingTextFieldId(entry.fieldId)}
                onRemove={() => handleRemove(entry.fieldId)}
              />
            ) : (
              <FieldPill
                key={entry.fieldId}
                fieldName={entry.fieldName}
                value={entry.value}
                onRemove={() => handleRemove(entry.fieldId)}
              />
            ),
          )}
        </div>
      )}

      {/* Search input */}
      <TextInput
        ref={inputRef}
        value={query}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder="Add field..."
      />

      {/* Search results popover */}
      <Popover
        anchorRef={inputRef}
        anchor="bottom-left"
        focusControlsRef={focusControlsRef}
        open={showPopover}
        onOutsideClick={closePopover}
        onOutsideFocus={closePopover}
      >
        <PopoverContents ref={popoverRef}>
          {searchResults.map((result, index) => (
            <PopoverItem
              key={`${result.fieldId}:${result.value || '__text__'}`}
              isSelected={selectedIndex === index}
              ref={selectedIndex === index ? activeItemRef : null}
              onClick={() => handleSelect(result)}
              className="cursor-pointer text-sm"
            >
              {result.fieldType === FieldType.Text ? (
                <>
                  <span className="font-medium">{result.fieldName}</span>
                  <span className="text-muted-foreground ml-1.5 text-xs">
                    (text)
                  </span>
                </>
              ) : (
                <>
                  <span className="text-muted-foreground">
                    {result.fieldName}:
                  </span>{' '}
                  <span className="font-medium">{result.value}</span>
                </>
              )}
            </PopoverItem>
          ))}
        </PopoverContents>
      </Popover>
    </div>
  );
};
